import type { RouteLocation, RouteLocationNormalized } from "vue-router";

import { isHttpError } from "@/backend/http/api.config.axios";
import type { HttpError } from "@/backend/http/api.config.axios";
import Auth from "@/backend/http/auth";
import { loggedIn } from "@/backend/read/BackendReader";
import type { UserResponse } from "@/backend/serverModel";
import { CancelError } from "@/error/CancelError";
import { captureException, captureMessage } from "@/error/sentry";
import { technicalUser } from "@/model/user";
import { useLoadingStore } from "@/store/loading";
import { SharedTask } from "@/utils/async/SharedTask";
import { isBrowserTabActive } from "@/utils/dom/dom";
import { checkShouldAutoLogout, cleanupInactivityStorage } from "@/utils/inactivityLogout";

import { establishSession, loginTarget } from "../login";
import { goToLogout } from "../navigation";
import { companyParam } from "./utils";

const loginTask = new SharedTask<LoginResult>();

export async function assertLoggedIn(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  // Check if authentication is needed for route
  if (to.matched.some((route) => route.meta.needsAuth === false)) {
    // Attempt authentication if possible, but don't require it
    if (!loggedIn() && to.meta.optionalAuth) {
      try {
        await loginTask.run(login);
      } catch {
        // Auth unavailable — continue to route without session
      }
    }
    return;
  }

  if (loggedIn()) {
    return;
  }

  return await loginAndRedirect(to, from);
}

function loadSession(response: UserResponse) {
  const user = technicalUser(
    response.user_id,
    response.company,
    response.role,
    response.nats_subject_scheme ?? "v1",
  );
  return useLoadingStore().load("login", establishSession(user));
}

async function loginAndRedirect(route: RouteLocation, from: RouteLocation) {
  // Check if user should be logged out due to inactivity
  if (checkShouldAutoLogout()) {
    cleanupInactivityStorage();
    void goToLogout("inactivity");
    return false;
  }

  // In case we specify ?company=xxx as a query parameter, we should redirect the user directly to the login page.
  // This is used in case of SSO to direct the user directly to the login page where the can click on the SSO-button.
  // When the user never authenticated before, we don't recognize the e-mail thus need to
  // know the company (which in this case is passed as param).
  if (companyParam()) {
    return { name: "login" };
  }
  try {
    const result = await loginTask.run(login);
    switch (result.status) {
      case "loggedIn":
        // the redirect target is per navigation, so it must not be shared
        return loginTarget({ ...route.query, path: route.path });
      case "rejected":
        captureMessage("Login unsuccessful with non-error status code", {
          response: { ...result.response },
          from: routeInfo(from),
          to: routeInfo(route),
        });
        return { name: "login" };
      default:
        return { name: "login" };
    }
  } catch (e) {
    if (e instanceof CancelError) {
      return false;
    }
    // Any 401 - redirect without reporting to Sentry.
    if (is401(e)) {
      return { name: "login" };
    }
    // a page reload can be triggered even for a non-active browser tab
    // e.g. when the access token times out
    // in this case, just go to login without a sentry message
    if (isBrowserTabActive()) {
      captureException(e, { from: routeInfo(from), to: routeInfo(route) });
    }
    return { name: "login" };
  }
}

type LoginResult =
  | { status: "loggedIn" }
  | { status: "rejected"; response: UserResponse }
  | { status: "refreshFailed" };

// Shared by concurrent navigations, so it must stay navigation independent.
async function login(): Promise<LoginResult> {
  useLoadingStore().start();
  try {
    const response = await useLoadingStore().load("checkAuth", Auth.isAuthenticated());
    if (!response.success) {
      return { status: "rejected", response };
    }
    await loadSession(response);
    return { status: "loggedIn" };
  } catch (e) {
    // isAuthenticated() uses rawClient (no auth interceptor), so 401s throw directly.
    // TOKEN_EXPIRED: attempt a silent token refresh before giving up.
    if (!isTokenExpired(e)) {
      throw e;
    }
    try {
      await loadSession(await Auth.refreshToken({}));
      return { status: "loggedIn" };
    } catch {
      return { status: "refreshFailed" };
    }
  }
}

function routeInfo(route: RouteLocation) {
  return {
    originalPath: route.redirectedFrom?.fullPath,
    fullPath: route.fullPath,
    params: route.params,
    query: route.query,
  };
}

function isTokenExpired(e: unknown) {
  return is401(e) && e.response?.data?.error?.code === "TOKEN_EXPIRED";
}

function is401(e: unknown): e is HttpError<{ error?: { code?: string } }> {
  return isHttpError(e) && e.response?.status === 401;
}
